'use client'

import { Question } from '@/lib/questionnaires/Questionnaire'
import FormikDatePicker from '@/shared/forms/components/FormikDatePicker'
import FormikRadio from '@/shared/forms/components/FormikRadio'
import FormikTextInput, {
  FormikTextInputVariant,
} from '@/shared/forms/components/FormikTextInput'
import FormikCheckbox from '@/shared/forms/components/FormikCheckbox'
import { addDays, isAfter, isBefore } from 'date-fns'
import { FormikProps, useField, useFormikContext } from 'formik'
import { formatDate, parseDate } from '@/lib/utils'
import * as React from 'react'
import { useEffect } from 'react'
import DurationInput from '@/shared/forms/components/DurationInput'
import GooglePlacesAutocompleteInput from '@/shared/forms/components/GooglePlacesAutocompleteInput'
import { DetailedAddress } from '@/lib/types'

type Props<T> = {
  question: Question
  formikProps: FormikProps<T>

  // These are only used by the prescription form. I want to hook into the change event but there's no simple way to
  // do this via 'Formik' object sadly :(
  onChange?: (e: React.ChangeEvent<any>) => void
  onBlur?: (e: React.FocusEvent<any>) => void
}

export default function QuestionnaireInput<T>({
  question,
  formikProps,
  onChange,
  onBlur,
}: Props<T>) {
  switch (question.type) {
    case 'free-text':
      return (
        <>
          <FormikTextInput
            key={question.key}
            label={question.title}
            size="full"
            autocomplete=""
            variant={
              question.multiline
                ? FormikTextInputVariant.Multiline
                : FormikTextInputVariant.Text
            }
            name={question.key}
            optional={question.optional}
            onChange={onChange}
            onBlur={onBlur}
          />
        </>
      )
    case 'yes-no':
      return (
        <>
          <FormikRadio
            key={question.key}
            label={question.title}
            size="full"
            autocomplete={question.key}
            options={[
              { id: 'Yes', label: 'Yes' },
              { id: 'No', label: 'No' },
            ]}
            name={question.key}
            onChange={onChange}
            onBlur={onBlur}
          />
        </>
      )
    case 'yes-no-boolean':
      return (
        <>
          <FormikRadio
            key={question.key}
            label={question.title}
            size="full"
            autocomplete={question.key}
            options={[
              {
                id: true,
                label: question.labels[0],
                disclaimer: question.disclaimer?.[0],
              },
              {
                id: false,
                label: question.labels[1],
                disclaimer: question.disclaimer?.[1],
              },
            ]}
            name={question.key}
            onChange={onChange}
            onBlur={onBlur}
          />
        </>
      )
    case 'single-choice':
      return (
        <>
          <FormikRadio
            key={question.key}
            label={question.title}
            size="full"
            autocomplete={question.key}
            options={question.options}
            name={question.key}
            onChange={onChange}
            onBlur={onBlur}
          />
        </>
      )
    case 'multiple-choice':
      return (
        <>
          <FormikCheckbox
            key={question.key}
            label={question.title}
            size="full"
            autocomplete={question.key}
            options={question.options}
            name={question.key}
            onChange={onChange}
            onBlur={onBlur}
          />
        </>
      )
    case 'duration':
      return <Duration question={question} onChange={onChange} />

    // TODO: These needs to take in the potential onChange and onBlur
    case 'date-range':
      return <DateRangeGroup question={question} />
    case 'date':
      return <DateInput question={question} />
    case 'address':
      return (
        <GooglePlacesAutocompleteInput
          name={question.key}
          onPickAddress={(address: DetailedAddress) =>
            question.onPickAddress(address, formikProps)
          }
          label={question.title}
          size="full"
          autocomplete=""
          optional={question.optional}
        />
      )
    default:
      return <></>
  }
}

type DurationProps = {
  question: Question.Duration
  onChange?: (e: React.ChangeEvent<any>) => void
}

function Duration({ question, onChange }: DurationProps) {
  const [yearField, yearMeta] = useField(question.yearKey)
  const [monthField, monthMeta] = useField(question.monthKey)

  const { isSubmitting } = useFormikContext()
  const errors: Array<string> = []
  if (yearMeta.error) errors.push(yearMeta.error)
  if (monthMeta.error) errors.push(monthMeta.error)

  return (
    <DurationInput
      label={question.title}
      size="full"
      disabled={isSubmitting}
      optional={question.optional}
      error={errors.join(' ')}
      touched={yearMeta.touched && monthMeta.touched}
      yearInputProps={{
        id: question.yearKey,
        name: question.yearKey,
        onBlur: yearField.onBlur,
        onChange: onChange ? onChange : yearField.onChange,
        value: yearField.value,
      }}
      monthInputProps={{
        id: question.monthKey,
        name: question.monthKey,
        onBlur: monthField.onBlur,
        onChange: onChange ? onChange : monthField.onChange,
        value: monthField.value,
      }}
    />
  )
}

type DateRangeGroupProps = {
  question: Question.DateRange
}

function DateRangeGroup({ question }: DateRangeGroupProps) {
  const { values, setFieldValue, setErrors } = useFormikContext<any>()
  const selectedStartDate = values[question.startKey]!
  const selectedEndDate = values[question.endKey]!

  let minStartDate = undefined
  let maxStartDate = undefined
  let minEndDate = undefined
  let maxEndDate = undefined

  if (question.options) {
    const { minStart, maxStart, minRange, maxRange } = question.options
    const today = new Date()

    minStartDate = addDays(today, minStart)
    maxStartDate = addDays(today, maxStart)

    const currentStartDate = parseDate(selectedStartDate)
    minEndDate = addDays(currentStartDate, minRange)
    maxEndDate = addDays(currentStartDate, maxRange)
  }

  // If the end date is before, or after allowed range, set the end date to the max of allowed range
  useEffect(() => {
    if (question.options) {
      const start = parseDate(selectedStartDate)
      const end = parseDate(selectedEndDate)
      const minEndDate = addDays(start, question.options.minRange)
      const maxEndDate = addDays(start, question.options.maxRange)

      if (isBefore(end, minEndDate)) {
        setFieldValue(question.endKey, formatDate(minEndDate), true).then(
          (errs) => {
            if (errs) setErrors(errs)
          },
        )
      } else if (isAfter(end, maxEndDate)) {
        setFieldValue(question.endKey, formatDate(maxEndDate), true).then(
          (errs) => {
            if (errs) setErrors(errs)
          },
        )
      }
    }
  }, [
    selectedStartDate,
    selectedEndDate,
    question.options,
    setFieldValue,
    setErrors,
    question.endKey,
  ])

  // If the end date is before the start date, set the end date to start date
  useEffect(() => {
    const start = parseDate(selectedStartDate)
    const end = parseDate(selectedEndDate)
    if (isBefore(end, start)) {
      setFieldValue(question.endKey, selectedStartDate, true).then((errs) => {
        if (errs) setErrors(errs)
      })
    }
  }, [
    selectedStartDate,
    selectedEndDate,
    setFieldValue,
    setErrors,
    question.endKey,
  ])

  return (
    <>
      <FormikDatePicker
        key={question.startKey}
        label={question.startTitle}
        size="1/2"
        autocomplete={question.startKey}
        variant="day-only"
        name={question.startKey}
        fromDate={minStartDate}
        toDate={maxStartDate}
      />
      <FormikDatePicker
        key={question.endKey}
        label={question.endTitle}
        size="1/2"
        autocomplete={question.endKey}
        variant="day-only"
        name={question.endKey}
        fromDate={minEndDate}
        toDate={maxEndDate}
      />
    </>
  )
}

type DateInputProps = {
  question: Question.Date
}

function DateInput({ question }: DateInputProps) {
  let minDay, maxDay

  if (question.options) {
    const { min, max } = question.options
    const today = new Date()
    minDay = addDays(today, min)
    maxDay = addDays(today, max)
  }

  return (
    <>
      <FormikDatePicker
        key={question.key}
        label={question.title}
        name={question.key}
        size="1/2"
        autocomplete=""
        variant="day-only"
        fromDate={minDay}
        toDate={maxDay}
      />
    </>
  )
}
